import React, { useState, useEffect, useCallback, useRef } from "react";
import useNavigation from "lib/hooks/useNavigation";
import NavigationPrompt from "components/common/NavigationPrompt";
import { useLocation } from "react-router-dom";
import { LocationDescriptor } from "history";

export const NavigationContext = React.createContext<NavigationContextValue>({
  navigation: [],
  activePath: undefined,
  isVerticalOpen: false,
  workflowDisabled: false,
  userOpExists: false,
  enableCustomDashboard: false,
  setUserOpExists: () => undefined,
  setEnableCustomDashboard: () => undefined,
  setWorkflowDisabled: () => undefined,
  setVerticalOpen: () => undefined,
  toggleVerticalOpen: () => undefined,
});

export const PreventNavigationContext =
  React.createContext<PreventNavigationContextValue>({
    preventNavigation: false,
    setPreventNavigation: () => undefined,
    askNavigateTo: () => true,
  });

export const NavigationProvider: React.FC<{
  navigation: NavigationData[];
  children: React.ReactNode;
}> = ({ children, navigation }) => {
  const { pathname } = useLocation();
  const { activePath, isVerticalOpen, setVerticalOpen, toggleVerticalOpen } =
    useNavigation(navigation);

  const [preventNavigation, setPreventNavigation] = useState(false);
  const [navigationPromptOpen, setNavigationPromptOpen] = useState(false);
  const [navigationPromptLocation, setNavigationPromptLocation] = useState<
    string | LocationDescriptor | (() => void) | null
  >(null);
  const [workflowDisabled, setWorkflowDisabled] = useState<boolean>(false);
  const [userOpExists, setUserOpExists] = useState<boolean>(false);
  const [enableCustomDashboard, setEnableCustomDashboard] =
    useState<boolean>(false);

  const askNavigateTo = useCallback((path?: string | (() => void)) => {
    if (path != null) {
      setNavigationPromptLocation(() => {
        if (typeof path === "function") {
          return () => {
            setPreventNavigation(false);
            path();
          };
        }

        return path;
      });
      setNavigationPromptOpen(true);
    }
  }, []);

  useEffect(() => {
    setPreventNavigation(false);
  }, [pathname]);

  // we delay the blocking of navigation,
  // preventing it to happen too early while the router is still transitioning
  const timeoutRef = useRef<NodeJS.Timeout>();
  const delayedSetPreventNavigation = useCallback((newState: boolean) => {
    if (timeoutRef.current != null) {
      clearTimeout(timeoutRef.current);
    }

    if (newState === false) {
      setPreventNavigation(false);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      setPreventNavigation(newState);
      timeoutRef.current = undefined;
    }, 500);
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <NavigationContext.Provider
      value={{
        navigation,
        activePath,
        isVerticalOpen,
        workflowDisabled,
        userOpExists,
        enableCustomDashboard,
        setEnableCustomDashboard,
        setWorkflowDisabled,
        setUserOpExists,
        toggleVerticalOpen,
        setVerticalOpen,
      }}
    >
      <PreventNavigationContext.Provider
        value={{
          preventNavigation,
          setPreventNavigation: delayedSetPreventNavigation,
          askNavigateTo,
        }}
      >
        {children}

        {preventNavigation && (
          <NavigationPrompt
            open={navigationPromptOpen}
            setOpen={setNavigationPromptOpen}
            location={navigationPromptLocation}
            setLocation={setNavigationPromptLocation}
          />
        )}
      </PreventNavigationContext.Provider>
    </NavigationContext.Provider>
  );
};
